import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type { AiFeedItem, SnomedCodeSelection } from "./types";

export interface AcceptFeedItemBody {
  selectedCode: SnomedCodeSelection | null;
}

export interface AcceptAiFeedItemResponse {
  aiFeedItem: AiFeedItem;
}

interface AcceptAiFeedItemVariables {
  itemId: number;
  selectedCode: SnomedCodeSelection | null;
}

export const useAcceptAiFeedItem = (
  patientId: string,
  encounterId: string,
  itemId: number,
) => {
  const queryClient = useQueryClient();

  return useApiMutation<AcceptAiFeedItemResponse, AcceptAiFeedItemVariables>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.AiFeed.ACCEPT(
        patientId,
        encounterId,
        itemId,
      ),
      mutationFn: async ({ itemId: mutationItemId, selectedCode }) => {
        const response = await axiosInstance.post<AcceptAiFeedItemResponse>(
          `/charting/ai-feed/items/${mutationItemId}/accept`,
          { selectedCode },
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.AiFeed.DEFAULT(
            patientId,
            encounterId,
          ),
        });
      },
      onError: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.AiFeed.DEFAULT(
            patientId,
            encounterId,
          ),
        });
        toast.error(
          "Something went wrong accepting an AI Feed item. Please try again.",
        );
      },
    },
  });
};
